import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Users, Baby, HeartHandshake, Eye, Star, Clock, CheckCircle2, Edit3 } from "lucide-react";
import { API_URL, BRAND } from "../lib/brand";
import KitDownloadModal from "../components/KitDownloadModal";

const STATUS_STYLE = {
  draft: { label: "Bozza", color: "#9CA3AF", icon: <Edit3 size={12} /> },
  scheduled: { label: "Programmata", color: "#F59E0B", icon: <Clock size={12} /> },
  published: { label: "Pubblicata", color: "#10B981", icon: <CheckCircle2 size={12} /> },
};

const AUDIENCE_LABEL = {
  genitori: { label: "Genitori", bg: "bg-[#FF2768]/10", text: "text-[#FF2768]", Icon: HeartHandshake },
  tate: { label: "Tate", bg: "bg-[#81C3C9]/25", text: "text-[#4E9EA5]", Icon: Baby },
  entrambi: { label: "Genitori & Tate", bg: "bg-[#FFE45C]/30", text: "text-[#3D2140]", Icon: Users },
};

export default function AdminResourcePreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const token = localStorage.getItem("tv_admin_token");

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    axios
      .get(`${API_URL}/admin/resources/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setResource(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem("tv_admin_token");
          navigate("/admin/login");
        } else setError("Risorsa non trovata");
      });
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="font-editorial italic text-[#3D2140]/70 text-xl mb-4">{error}</p>
          <Link to="/admin/risorse" className="text-[#FF2768] font-semibold hover:underline">← Torna alle risorse</Link>
        </div>
      </div>
    );
  }
  if (!resource) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <p className="font-editorial italic text-[#3D2140]/60 text-xl">Sto caricando l'anteprima…</p>
      </div>
    );
  }

  const status = STATUS_STYLE[resource.status] || STATUS_STYLE.draft;
  const audience = AUDIENCE_LABEL[resource.category] || AUDIENCE_LABEL.entrambi;
  const AudienceIcon = audience.Icon;
  const publishDate = new Date(resource.publish_at);
  const publishFmt = publishDate.toLocaleDateString("it-IT", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  const publishTime = publishDate.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

  const modalConfig = {
    resourceId: resource.id,
    kitName: resource.title,
    downloadFileName: `${resource.title.replace(/[^a-zA-Z0-9]+/g, "-")}.pdf`,
    downloadUrl: resource.pdf_url,
    title: resource.title.split(" ").slice(0, 2).join(" "),
    titleLine2: resource.title.split(" ").slice(2).join(" ") || resource.subtitle || "",
    subtitle: "La tua risorsa",
    intro: "Compila il modulo per ricevere la tua risorsa 💌",
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="admin-preview-page">
      {/* Preview banner */}
      <div className="bg-[#3D2140] text-white sticky top-0 z-40" data-testid="preview-banner">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Eye size={16} className="text-[#FFE45C]" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] font-semibold">Anteprima Admin</p>
              <p className="text-xs text-white/70">Ecco come vedranno questa scheda i tuoi lettori</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold" style={{ background: status.color, color: "#fff" }}>
              {status.icon} {status.label}
            </span>
            <Link to={`/admin/risorse`} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold border border-white/20" data-testid="preview-back">
              <ArrowLeft size={14} /> Torna alla lista
            </Link>
          </div>
        </div>
      </div>

      {/* Metadata card */}
      <div className="max-w-[1400px] mx-auto px-6 pt-6">
        <div className="bg-white rounded-2xl p-5 border border-[#3D2140]/8 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="preview-metadata">
          <MetaItem label="Pubblicazione" value={publishFmt} sub={`ore ${publishTime}`} />
          <MetaItem label="Categoria" value={audience.label} />
          <MetaItem label="Download" value={resource.downloads_count || 0} sub="finora" />
          <MetaItem label="Consensi marketing" value={resource.consent_count || 0} sub="ricevuti" />
        </div>
      </div>

      {/* Card preview — replicates public Risorse card */}
      <section className="max-w-[1400px] mx-auto px-6 py-10">
        <p className="text-xs uppercase tracking-widest text-[#3D2140]/50 mb-4">↓ Vista card come nella pagina pubblica /risorse</p>
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-xl p-8 md:p-10 rounded-[32px] bg-white border border-[#81C3C9]/40 hover:border-[#FF2768] transition-colors duration-500 flex flex-col"
          data-testid="preview-card"
        >
          <div className="flex items-center gap-2 mb-6">
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${audience.bg} ${audience.text}`}>
              <AudienceIcon size={12} /> {audience.label}
            </span>
            {resource.featured && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-[#FF2768]/10 text-[#FF2768]">
                <Star size={12} className="fill-[#FF2768]" /> In evidenza
              </span>
            )}
            <span className="text-[10px] uppercase tracking-widest text-[#81C3C9] font-semibold">
              · {resource.subtitle || "Biblioteca T.A.T.A."}
            </span>
          </div>

          <div className="flex items-start gap-4 mb-6">
            <span className="h-14 w-14 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center shrink-0">
              <Download size={22} />
            </span>
            <h3 className="font-display text-3xl md:text-4xl text-[#3D2140] leading-[0.95]">
              {resource.title}
            </h3>
          </div>

          <p className="text-[#3D2140]/75 leading-relaxed mb-8 flex-1">
            {resource.description || <em className="text-[#3D2140]/40">Nessuna descrizione impostata</em>}
          </p>

          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="preview-open-modal"
            className="inline-flex items-center gap-2 text-[#FF2768] font-semibold uppercase tracking-wider text-sm hover:underline text-left"
          >
            {resource.cta_text || "Scarica gratuitamente"} →
          </button>
        </motion.article>

        {/* Success quote preview */}
        {resource.success_quote && (
          <div className="mt-8 max-w-xl p-6 rounded-2xl bg-[#81C3C9]/20 border-l-4 border-[#FF2768]" data-testid="preview-quote">
            <p className="text-[10px] uppercase tracking-widest text-[#FF2768] font-semibold mb-2">— Frase mostrata dopo il download</p>
            <p className="font-editorial italic text-lg text-[#3D2140] leading-relaxed">"{resource.success_quote}"</p>
          </div>
        )}

        {/* Direct PDF link */}
        {resource.pdf_url && (
          <div className="mt-6">
            <a href={resource.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#3D2140]/70 hover:text-[#FF2768]" data-testid="preview-pdf-link">
              📄 Apri il PDF diretto (senza modal) →
            </a>
          </div>
        )}
      </section>

      {/* Info box if not yet public */}
      {resource.status !== "published" && (
        <div className="max-w-[1400px] mx-auto px-6 pb-10">
          <div className="p-5 rounded-2xl bg-[#FFE45C]/30 border border-[#F59E0B]/40 flex items-start gap-3">
            <Clock size={20} className="text-[#F59E0B] mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-[#3D2140]">Questa risorsa non è ancora visibile al pubblico</p>
              <p className="text-sm text-[#3D2140]/70 mt-1">
                Diventerà scaricabile automaticamente il <strong>{publishFmt}</strong> alle <strong>{publishTime}</strong>. Da qui puoi vedere l'anteprima esatta di come apparirà.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal preview (functional but download won't record because status check will block if not published) */}
      <KitDownloadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        {...modalConfig}
      />
    </div>
  );
}

function MetaItem({ label, value, sub }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 font-semibold">{label}</p>
      <p className="font-display text-xl text-[#3D2140] mt-1 leading-tight capitalize">{value}</p>
      {sub && <p className="text-xs text-[#3D2140]/50 mt-0.5">{sub}</p>}
    </div>
  );
}
